/* ==========================================================================
   Homnio — Bundle & Save selector
   - Native radio selection (keyboard accessible)
   - Adds the correct line items via the Ajax Cart API
   - Opens / refreshes Horizon's native <cart-drawer> using its own
     renderContents() + getSectionsToRender() mechanism (so the cart drawer,
     cart icon bubble, Shopify analytics and Meta Pixel all behave like a
     normal add-to-cart). Falls back to the cart page if no drawer exists.
   - Accessible, announced error handling that preserves the selection
   - Guards against repeated clicks while a request is in flight
   - Drives the optional sticky mobile bar (price + add) through the same path
   Pricing itself is handled server-side by Shopify automatic discounts —
   this script only adds real line items; it never fakes a price. When an
   offer carries an explicit data-variant-id (e.g. a real 2-pack variant),
   that variant is added at quantity 1 as a single clean line item.
   ========================================================================== */
(function () {
  'use strict';

  if (customElements.get('homnio-bundle')) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  customElements.define(
    'homnio-bundle',
    class HomnioBundle extends HTMLElement {
      connectedCallback() {
        this.submitting = false;
        this.offerInputs = Array.prototype.slice.call(this.querySelectorAll('.homnio-bundle__input'));
        this.colourInputs = Array.prototype.slice.call(this.querySelectorAll('.homnio-bundle__colour input'));
        this.submitBtn = this.querySelector('.homnio-bundle__submit');
        this.submitLabel = this.querySelector('[data-homnio-bundle-label]');
        this.errorEl = this.querySelector('.homnio-bundle__error');
        this.chosenEl = this.querySelector('.homnio-bundle__chosen');
        this.liftVariantId = parseInt(this.dataset.liftVariant, 10) || null;
        this.dynamic = this.dataset.dynamicButton === 'true';
        this.defaultLabel = this.submitLabel ? this.submitLabel.textContent : 'Add to Cart';

        this.offerInputs.forEach(
          function (input) {
            input.addEventListener('change', this.onOfferChange.bind(this));
            // Keep .is-selected / .is-focused in sync for browsers without :has()
            input.addEventListener('focus', function () {
              var card = input.closest('.homnio-bundle__card');
              if (card) card.classList.add('is-focused');
            });
            input.addEventListener('blur', function () {
              var card = input.closest('.homnio-bundle__card');
              if (card) card.classList.remove('is-focused');
            });
          }.bind(this)
        );

        this.colourInputs.forEach(
          function (input) {
            input.addEventListener('change', this.onColourChange.bind(this));
          }.bind(this)
        );

        if (this.submitBtn) this.submitBtn.addEventListener('click', this.onSubmit.bind(this));

        // Optional sticky mobile bar: drive its price + add the selected bundle
        // through the same path (no duplicate add-to-cart logic).
        this.stickyPrice = document.querySelector('[data-fr-sticky] [data-fr-sbprice]');
        this.stickyBtn = document.querySelector('[data-fr-sticky] [data-fr-atc]');
        if (this.stickyBtn) this.stickyBtn.addEventListener('click', this.onSubmit.bind(this));

        this.syncSelectedClass();
        this.syncButton();
      }

      /* ---- selection state ------------------------------------------------ */
      selectedOffer() {
        return this.offerInputs.filter(function (i) { return i.checked; })[0] || null;
      }

      selectedColourId() {
        var checked = this.colourInputs.filter(function (i) { return i.checked; })[0];
        if (checked && checked.dataset.variantId) return parseInt(checked.dataset.variantId, 10);
        return parseInt(this.dataset.erDefault, 10) || null;
      }

      onOfferChange() {
        this.clearError();
        this.syncSelectedClass();
        this.syncButton();
      }

      onColourChange() {
        var checked = this.colourInputs.filter(function (i) { return i.checked; })[0];
        if (checked && this.chosenEl) this.chosenEl.textContent = checked.value;
      }

      syncSelectedClass() {
        this.offerInputs.forEach(function (input) {
          var card = input.closest('.homnio-bundle__card');
          if (card) card.classList.toggle('is-selected', input.checked);
        });
      }

      syncButton() {
        if (!this.submitBtn) return;
        var offer = this.selectedOffer();
        if (!offer) {
          this.submitBtn.setAttribute('aria-disabled', 'true');
          if (this.submitLabel) this.submitLabel.textContent = this.defaultLabel;
          return;
        }
        this.submitBtn.removeAttribute('aria-disabled');
        if (this.submitLabel) {
          this.submitLabel.textContent = this.dynamic && offer.dataset.buttonLabel
            ? offer.dataset.buttonLabel
            : this.defaultLabel;
        }
        // Keep the sticky mobile bar's price in sync with the selected offer.
        if (this.stickyPrice && offer.dataset.total) this.stickyPrice.textContent = offer.dataset.total;
      }

      /* ---- items ---------------------------------------------------------- */
      itemsForSelection() {
        var offer = this.selectedOffer();
        if (!offer) return [];
        // An explicit per-offer variant id (e.g. a real 2-pack variant) takes
        // priority over the colour/default variant. It is added at its own
        // quantity (default 1) as a single, clean line item.
        var overrideId = offer.dataset.variantId ? parseInt(offer.dataset.variantId, 10) : null;
        var erId = overrideId || this.selectedColourId();
        var key = offer.value; // o1 / o2 / o3
        var erQty = parseInt(offer.dataset.erQty, 10) || 1;
        var needsLift = offer.dataset.needsLift === 'true';

        if (needsLift && !this.liftVariantId) return [];
        if (!erId) return [];

        // Private (underscore) properties: internal identification only, never pricing.
        // Skipped for single-unit offers (o1) and for real explicit variants,
        // so those add as ordinary, untagged line items.
        var props = (key === 'o1' || overrideId) ? undefined : { _homnio_bundle: key };

        var items = [{ id: erId, quantity: erQty }];
        if (props) items[0].properties = props;

        if (needsLift) {
          var lift = { id: this.liftVariantId, quantity: 1 };
          if (props) lift.properties = props;
          items.push(lift);
        }
        return items;
      }

      /* ---- add to cart ---------------------------------------------------- */
      cartElement() {
        return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      }

      onSubmit(evt) {
        if (evt) evt.preventDefault();
        if (this.submitting) return;
        if (this.submitBtn && this.submitBtn.getAttribute('aria-disabled') === 'true') return;

        var items = this.itemsForSelection();
        if (!items.length) {
          this.showError(this.dataset.errSelect || 'Please choose an option first.');
          return;
        }

        this.setLoading(true);
        this.clearError();

        var cart = this.cartElement();
        var body = { items: items };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
          body.sections_url = window.location.pathname;
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }

        var self = this;
        fetch(ROOT + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        })
          .then(function (res) {
            return res.json().then(function (data) { return { ok: res.ok, data: data }; });
          })
          .then(function (result) {
            if (!result.ok) {
              throw new Error((result.data && (result.data.description || result.data.message)) || '');
            }
            self.afterAdd(result.data, cart);
          })
          .catch(function (err) {
            self.setLoading(false);
            self.showError(
              (err && err.message) || self.dataset.errGeneric || 'Sorry, we could not add this to your cart. Please try again.'
            );
          });
      }

      afterAdd(data, cart) {
        // Let the rest of the theme (cart count, pixels, listeners) know.
        try {
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'homnio-bundle', cartData: data });
          }
        } catch (e) { /* non-fatal */ }

        this.setLoading(false);

        if (cart && typeof cart.renderContents === 'function') {
          // Mirror Horizon's product-form.js: clear the drawer's empty state
          // before opening, otherwise its focus-trap targets a null element.
          if (cart.classList && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(data); // refreshes drawer/notification + opens it
        } else {
          window.location.href = ROOT + 'cart';
        }
      }

      /* ---- ui helpers ----------------------------------------------------- */
      setLoading(on) {
        this.submitting = on;
        this.classList.toggle('is-loading', on);
        if (!this.submitBtn) return;
        if (on) {
          this.submitBtn.setAttribute('aria-disabled', 'true');
          this.submitBtn.setAttribute('aria-busy', 'true');
        } else {
          this.submitBtn.removeAttribute('aria-busy');
          this.syncButton();
        }
      }

      showError(msg) {
        if (!this.errorEl) return;
        this.errorEl.textContent = msg;
        this.errorEl.removeAttribute('hidden');
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.setAttribute('hidden', '');
        this.errorEl.textContent = '';
      }
    }
  );
})();